import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Wifi, Power, RefreshCw, Trash2, Download, Plus, Settings, CheckSquare, Square, Zap, Users, ArrowUpDown, ArrowUp, ArrowDown, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import ConfirmDialog from '@/components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';

const WANDevices = () => {
  const { user: _bsUser } = useAuth();
  const _bsUserBranchId = _bsUser?.branch_id ? String(_bsUser.branch_id) : null;
  const _isBranchScoped = ['kepala_cabang', 'teknisi', 'customer service'].includes(String(_bsUser?.role || '').toLowerCase()) && _bsUserBranchId;
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  // Read '?device=<id>' from URL (e.g. coming from Customer Detail 'Configure')
  // Initial value seeds the search filter so the list is already scoped on first fetch.
  const initialDeviceFilter = (() => {
    if (typeof window === 'undefined') return '';
    const sp = new URLSearchParams(window.location.search);
    return sp.get('device') || '';
  })();
  const [search, setSearch] = useState(initialDeviceFilter);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showWiFiModal, setShowWiFiModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [acsDevices, setAcsDevices] = useState([]);
  const [selectedForImport, setSelectedForImport] = useState([]);
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [newDevice, setNewDevice] = useState({
    serial_number: '',
    device_id: '',
    model: 'FiberHome',
    manufacturer: 'FiberHome',
    tr069_ip: '',
    mac_address: '',
    firmware_version: '',
    customer_id: '',
    pppoe_username: '',
    notes: ''
  });
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalDevices, setTotalDevices] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Stats from backend
  const [stats, setStats] = useState({
    total_all: 0,
    online: 0,
    offline: 0,
    with_customer: 0
  });
  
  // Filter state - default to 'all' to show all devices
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'online', 'offline'
  
  // Sorting states
  const [sortField, setSortField] = useState(null); // field to sort by
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  
  // Bulk operations states
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [showBulkRebootModal, setShowBulkRebootModal] = useState(false);
  const [showBulkWiFiModal, setShowBulkWiFiModal] = useState(false);
  const [bulkOperationInProgress, setBulkOperationInProgress] = useState(false);
  const [bulkOperationResults, setBulkOperationResults] = useState(null);
  
  // Data source tracking
  const [dataSource, setDataSource] = useState('acs_realtime'); // 'acs_realtime' or 'cache'
  
  // Auto-sync states
  const [syncSettings, setSyncSettings] = useState(null);
  const [showSyncSettings, setShowSyncSettings] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Remote access states
  const [showRemoteModal, setShowRemoteModal] = useState(false);
  const [remoteDevice, setRemoteDevice] = useState(null);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteUrl, setRemoteUrl] = useState('');
  
  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    type: 'warning',
    action: 'default',
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: null,
    loading: false
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [searchParams, setSearchParams] = useSearchParams();
  const deviceFromUrl = searchParams.get('device') || '';
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');

  useEffect(() => {
    fetchDevices();
  }, [currentPage, pageSize, selectedBranchId]);

  // Fetch list of branches for filter dropdown (try tenant-scoped first, fallback to superadmin)
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const token = localStorage.getItem('token');
        let res = await fetch(`${API_URL}/api/branches`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        let data = res.ok ? await res.json() : null;
        let list = data?.data || [];
        if ((!list || list.length === 0)) {
          // Fallback for super_admin only (skip for tenant admin to avoid 403)
          let isSuperAdmin = false;
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const role = (payload.role || '').toLowerCase();
            isSuperAdmin = role === 'super_admin' || role === 'superadmin';
          } catch (e) {}
          if (isSuperAdmin) {
            try {
              res = await fetch(`${API_URL}/api/v2/superadmin/branches`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              data = res.ok ? await res.json() : null;
              list = data?.data || data || [];
            } catch {}
          }
        }
        setBranches(Array.isArray(list) ? list : []);
      } catch (e) {
        console.log('Failed to load branches', e);
      }
    };
    fetchBranches();
  }, []);

  // Deterministic color palette for branch badge (same branch → same color across rows)
  const branchBadgeColor = (name) => {
    if (!name) return 'bg-slate-100 text-slate-600 border-slate-200';
    const palette = [
      'bg-blue-50 text-blue-700 border-blue-200',
      'bg-emerald-50 text-emerald-700 border-emerald-200',
      'bg-amber-50 text-amber-700 border-amber-200',
      'bg-purple-50 text-purple-700 border-purple-200',
      'bg-rose-50 text-rose-700 border-rose-200',
      'bg-cyan-50 text-cyan-700 border-cyan-200',
      'bg-indigo-50 text-indigo-700 border-indigo-200',
      'bg-orange-50 text-orange-700 border-orange-200',
      'bg-teal-50 text-teal-700 border-teal-200',
      'bg-pink-50 text-pink-700 border-pink-200',
    ];
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
    return palette[h % palette.length];
  };

  const clearDeviceFilter = () => {
    setSearch('');
    setCurrentPage(1);
    const next = new URLSearchParams(searchParams);
    next.delete('device');
    setSearchParams(next, { replace: true });
  };

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const branchParam = selectedBranchId ? `&branch_id=${encodeURIComponent(selectedBranchId)}` : '';
      const response = await fetch(
        `${API_URL}/api/wan-devices?page=${currentPage}&page_size=${pageSize}${searchParam}${branchParam}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setDevices(data.data.devices || []);
        setTotalDevices(data.data.total || 0);
        setTotalPages(data.data.total_pages || 0);
        if (data.data.stats) {
          setStats(data.data.stats);
        }
        // Track data source
        if (data.data.data_source) {
          setDataSource(data.data.data_source);
          
          // Show warning based on data source
          if (data.data.data_source === 'cache') {
            toast.warning('⚠️ Menampilkan data cache - ACS server tidak dapat diakses', {
              duration: 5000
            });
          } else if (data.data.data_source === 'acs_offline') {
            toast.error('❌ ACS server OFFLINE - Semua device ditandai sebagai OFFLINE', {
              duration: 6000
            });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
      toast.error('Failed to load devices');
    } finally {
      setLoading(false);
    }
  };

  const fetchACSDevices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/acs/devices?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAcsDevices(data.data || []);
        setShowImportModal(true);
      }
    } catch (error) {
      console.error('Error fetching ACS devices:', error);
      toast.error('Failed to load ACS devices');
    }
  };

  const importDevices = async () => {
    if (selectedForImport.length === 0) {
      toast.error('Please select devices to import');
      return;
    }

    try {
      setImporting(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/acs/devices/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ device_ids: selectedForImport })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowImportModal(false);
        setSelectedForImport([]);
        fetchDevices();
      } else {
        toast.error('Import failed');
      }
    } catch (error) {
      console.error('Error importing:', error);
      toast.error('Import failed');
    } finally {
      setImporting(false);
    }
  };

  const rebootDevice = (deviceId) => {
    // Find device info for better messaging
    const device = devices.find(d => d.device_id === deviceId);
    const deviceName = device ? device.serial_number : deviceId;

    setConfirmDialog({
      isOpen: true,
      type: 'warning',
      action: 'reboot',
      title: 'Reboot Device',
      message: `Are you sure you want to reboot device "${deviceName}"? This will temporarily disconnect the device from the network and may interrupt ongoing connections.`,
      confirmText: 'Reboot Device',
      onConfirm: () => performSingleReboot(deviceId),
      loading: false
    });
  };

  const performSingleReboot = async (deviceId) => {
    try {
      setConfirmDialog(prev => ({ ...prev, loading: true }));
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/wan-devices/${deviceId}/reboot`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Reboot command sent successfully');
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      } else {
        toast.error('Reboot failed');
        setConfirmDialog(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error rebooting:', error);
      toast.error('Reboot failed');
      setConfirmDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const updateWiFi = async (wifiData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/wan-devices/${selectedDevice.device_id}/wifi-config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(wifiData)
      });

      if (response.ok) {
        toast.success('WiFi settings updated successfully');
        setShowWiFiModal(false);
        fetchDevices();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update WiFi');
      }
    } catch (error) {
      console.error('Error updating WiFi:', error);
      toast.error('Failed to update WiFi');
    }
  };

  // Search handler with debounce
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1); // Reset to first page on search
  };

  // Trigger fetch when search changes (with delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage === 1) {
        fetchDevices();
      } else {
        setCurrentPage(1);
      }
    }, 500); // 500ms debounce
    
    return () => clearTimeout(timer);
  }, [search]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page
  };

  // Sorting handler
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort devices function
  const getSortedDevices = (devicesToSort) => {
    if (!sortField) return devicesToSort;

    return [...devicesToSort].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      // Special handling for status (online/offline)
      if (sortField === 'status') {
        aVal = a.online ? 'online' : 'offline';
        bVal = b.online ? 'online' : 'offline';
      }

      // Convert to string for comparison
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
  };

  // Render sort icon
  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3 h-3 ml-1 text-slate-400 dark:text-slate-500" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-indigo-600 dark:text-indigo-400" />
      : <ArrowDown className="w-3 h-3 ml-1 text-indigo-600 dark:text-indigo-400" />;
  };

  // Handle remote access - using port-based proxy (like old billing system)
  const handleRemoteAccess = async (device) => {
    if (!device.external_ip) {
      toast.error('Device tidak memiliki IP address');
      return;
    }
    
    setRemoteDevice(device);
    setRemoteLoading(true);
    setShowRemoteModal(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Create port-based proxy (dedicated port for this device)
      const response = await fetch(`${API_URL}/api/v2/remote-ont/create`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ont_ip: device.external_ip,
          ont_port: 80
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data.public_url) {
          setRemoteUrl(data.data.public_url);
          toast.success(`Remote access ready! Port: ${data.data.public_port}`);
        } else {
          toast.error(data.data?.message || 'Gagal membuat port proxy');
          setRemoteUrl('');
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Device tidak dapat dijangkau');
        setRemoteUrl('');
      }
    } catch (error) {
      console.error('Error creating remote access:', error);
      toast.error('Error membuat akses remote');
      setRemoteUrl('');
    } finally {
      setRemoteLoading(false);
    }
  };

  // Open remote access in new tab
  const openRemoteInNewTab = () => {
    if (remoteUrl && remoteDevice) {
      // Open direct port proxy URL in new tab
      window.open(remoteUrl, '_blank');
    }
  };

  // Fetch sync settings
  const fetchSyncSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/wan/sync/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSyncSettings(data.data);
      }
    } catch (error) {
      console.error('Error fetching sync settings:', error);
    }
  };

  // Manual sync trigger
  const triggerManualSync = () => {
    setConfirmDialog({
      isOpen: true,
      type: 'info',
      action: 'default',
      title: 'Manual Sync',
      message: 'Sync all devices from ACS server? This will update device status, IP addresses, and configuration. The process may take a few seconds.',
      confirmText: 'Start Sync',
      onConfirm: performManualSync,
      loading: false
    });
  };

  const performManualSync = async () => {
    try {
      setConfirmDialog(prev => ({ ...prev, loading: true }));
      setSyncing(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/wan/sync/now`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchDevices();
        fetchSyncSettings();
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      } else {
        toast.error('Sync failed');
        setConfirmDialog(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error('Sync failed');
      setConfirmDialog(prev => ({ ...prev, loading: false }));
    } finally {
      setSyncing(false);
    }
  };

  // Bulk selection handlers
  const handleSelectDevice = (deviceId) => {
    setSelectedDevices(prev => {
      if (prev.includes(deviceId)) {
        return prev.filter(id => id !== deviceId);
      } else {
        return [...prev, deviceId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedDevices.length === devices.length) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices(devices.map(d => d.device_id));
    }
  };

  // Bulk reboot
  const handleBulkReboot = () => {
    if (selectedDevices.length === 0) {
      toast.error('No devices selected');
      return;
    }

    setConfirmDialog({
      isOpen: true,
      type: 'warning',
      action: 'reboot',
      title: 'Reboot Devices',
      message: `Are you sure you want to reboot ${selectedDevices.length} selected device${selectedDevices.length > 1 ? 's' : ''}? This action will temporarily disconnect the device${selectedDevices.length > 1 ? 's' : ''} from the network.`,
      confirmText: `Reboot ${selectedDevices.length} Device${selectedDevices.length > 1 ? 's' : ''}`,
      onConfirm: performBulkReboot,
      loading: false
    });
  };

  const performBulkReboot = async () => {
    try {
      setConfirmDialog(prev => ({ ...prev, loading: true }));
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/wan/bulk-reboot`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ device_ids: selectedDevices })
      });

      if (response.ok) {
        const data = await response.json();
        setBulkOperationResults(data.data);
        toast.success(data.message);
        setSelectedDevices([]);
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      } else {
        toast.error('Bulk reboot failed');
        setConfirmDialog(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error bulk reboot:', error);
      toast.error('Bulk reboot failed');
      setConfirmDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Bulk summon/refresh
  const handleBulkSummon = () => {
    if (selectedDevices.length === 0) {
      toast.error('No devices selected');
      return;
    }

    setConfirmDialog({
      isOpen: true,
      type: 'info',
      action: 'summon',
      title: 'Summon Devices',
      message: `Are you sure you want to summon/refresh ${selectedDevices.length} selected device${selectedDevices.length > 1 ? 's' : ''}? This will trigger a connection request to fetch the latest device data.`,
      confirmText: `Summon ${selectedDevices.length} Device${selectedDevices.length > 1 ? 's' : ''}`,
      onConfirm: performBulkSummon,
      loading: false
    });
  };

  const performBulkSummon = async () => {
    try {
      setConfirmDialog(prev => ({ ...prev, loading: true }));
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/wan/bulk-summon`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ device_ids: selectedDevices })
      });

      if (response.ok) {
        const data = await response.json();
        setBulkOperationResults(data.data);
        toast.success(data.message);
        setSelectedDevices([]);
        fetchDevices(); // Refresh table
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      } else {
        toast.error('Bulk summon failed');
        setConfirmDialog(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error bulk summon:', error);
      toast.error('Bulk summon failed');
      setConfirmDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Summon all online devices
  const handleSummonAll = () => {
    setConfirmDialog({
      isOpen: true,
      type: 'warning',
      action: 'summon_all',
      title: 'Summon All Online Devices',
      message: `Are you sure you want to summon ALL online devices (${stats.online} devices)? This will trigger connection requests to all online devices and may take some time.`,
      confirmText: `Summon All ${stats.online} Devices`,
      onConfirm: performSummonAll,
      loading: false
    });
  };

  const performSummonAll = async () => {
    try {
      setConfirmDialog(prev => ({ ...prev, loading: true }));
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/wan/summon-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBulkOperationResults(data.data);
        toast.success(data.message);
        fetchDevices(); // Refresh table
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      } else {
        toast.error('Summon all failed');
        setConfirmDialog(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error summon all:', error);
      toast.error('Summon all failed');
      setConfirmDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Summon single device
  const handleSummonDevice = async (deviceId) => {
    try {
      const token = localStorage.getItem('token');
      toast.loading('Summoning device...', { id: 'summon' });
      
      const response = await fetch(`${API_URL}/api/wan-devices/${deviceId}/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Device summoned successfully', { id: 'summon' });
        fetchDevices(); // Refresh table
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to summon device', { id: 'summon' });
      }
    } catch (error) {
      console.error('Error summon device:', error);
      toast.error('Failed to summon device', { id: 'summon' });
    }
  };

  // Bulk delete
  const handleBulkDelete = () => {
    if (selectedDevices.length === 0) {
      toast.error('No devices selected');
      return;
    }

    setConfirmDialog({
      isOpen: true,
      type: 'danger',
      action: 'delete',
      title: 'Delete Devices',
      message: `Are you sure you want to permanently delete ${selectedDevices.length} selected device${selectedDevices.length > 1 ? 's' : ''}? This action cannot be undone and will remove all device data from the database.`,
      confirmText: `Delete ${selectedDevices.length} Device${selectedDevices.length > 1 ? 's' : ''}`,
      onConfirm: performBulkDelete,
      loading: false
    });
  };

  const performBulkDelete = async () => {
    try {
      setConfirmDialog(prev => ({ ...prev, loading: true }));
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/wan/bulk-delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ device_ids: selectedDevices })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setSelectedDevices([]);
        fetchDevices(); // Refresh table
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Bulk delete failed');
        setConfirmDialog(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error bulk delete:', error);
      toast.error('Bulk delete failed');
      setConfirmDialog(prev => ({ ...prev, loading: false }));
    }
  };


  // Bulk WiFi config
  const handleBulkWiFiConfig = async (wifiData) => {
    if (selectedDevices.length === 0) {
      toast.error('No devices selected');
      return;
    }

    try {
      setBulkOperationInProgress(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/wan/bulk-wifi-config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          device_ids: selectedDevices,
          ...wifiData
        })
      });

      if (response.ok) {
        const data = await response.json();
        setBulkOperationResults(data.data);
        toast.success(data.message);
        setSelectedDevices([]);
      } else {
        toast.error('Bulk WiFi config failed');
      }
    } catch (error) {
      console.error('Error bulk WiFi config:', error);
      toast.error('Bulk WiFi config failed');
    } finally {
      setBulkOperationInProgress(false);
      setShowBulkWiFiModal(false);
    }
  };

  // Load sync settings on mount
  useEffect(() => {
    fetchSyncSettings();
  }, []);

  // Handle Add Device
  const handleAddDevice = async () => {
    // Validation
    if (!newDevice.serial_number || !newDevice.device_id) {
      toast.error('Serial Number dan Device ID wajib diisi');
      return;
    }

    if (!newDevice.tr069_ip) {
      toast.error('TR069 IP Address wajib diisi');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/wan-devices`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newDevice)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Device berhasil ditambahkan');
        setShowAddDeviceModal(false);
        // Reset form
        setNewDevice({
          serial_number: '',
          device_id: '',
          model: 'FiberHome',
          manufacturer: 'FiberHome',
          tr069_ip: '',
          mac_address: '',
          firmware_version: '',
          customer_id: '',
          pppoe_username: '',
          notes: ''
        });
        fetchDevices(); // Refresh list
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Gagal menambahkan device');
      }
    } catch (error) {
      console.error('Error adding device:', error);
      toast.error('Gagal menambahkan device');
    }
  };


  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-3 sm:p-6 space-y-4 sm:space-y-6">
      {/* Filtered-from-customer context banner */}
      {deviceFromUrl && (
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/30" data-testid="wan-device-filter-banner">
          <div className="flex items-center gap-2 text-sm text-blue-800">
            <Settings size={16} className="flex-shrink-0" />
            <span>
              Menampilkan konfigurasi untuk device:{' '}
              <span className="font-semibold font-mono">{deviceFromUrl}</span>
            </span>
          </div>
          <button
            type="button"
            onClick={clearDeviceFilter}
            className="text-xs font-medium text-blue-600 hover:text-white hover:bg-blue-600 border border-blue-200 hover:border-blue-600 px-3 py-1 rounded transition-all whitespace-nowrap dark:text-blue-400"
            data-testid="wan-device-filter-clear-btn"
          >
            Lihat Semua Device
          </button>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold">WAN Devices</h1>
          <p className="text-xs sm:text-sm text-slate-600 mt-1 dark:text-slate-300">
            Manage ONT/CPE via TR-069 
            {dataSource === 'acs_realtime' ? (
              <span className="ml-1 text-green-600 font-medium dark:text-green-400">• Real-time</span>
            ) : dataSource === 'acs_offline' ? (
              <span className="ml-1 text-red-600 font-medium dark:text-red-400">❌ ACS Offline</span>
            ) : (
              <span className="ml-1 text-orange-600 font-medium dark:text-orange-400">⚠️ Cache</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {selectedDevices.length > 0 && (
            <>
              <Button onClick={handleBulkDelete} variant="outline" size="sm" className="border-red-500 text-red-600 hover:bg-red-50 text-xs h-8 dark:text-red-400 dark:hover:bg-red-900/30">
                <Trash2 className="w-3 h-3 sm:mr-1" />
                <span className="hidden sm:inline">Delete</span> ({selectedDevices.length})
              </Button>
              <Button onClick={handleBulkReboot} variant="outline" size="sm" className="border-orange-500 text-orange-600 text-xs h-8 dark:text-orange-400">
                <Power className="w-3 h-3 sm:mr-1" />
                <span className="hidden sm:inline">Reboot</span> ({selectedDevices.length})
              </Button>
              <Button onClick={handleBulkSummon} variant="outline" size="sm" className="border-green-500 text-green-600 hover:bg-green-50 text-xs h-8 dark:text-green-400 dark:hover:bg-green-900/30">
                <Zap className="w-3 h-3 sm:mr-1" />
                <span className="hidden sm:inline">Summon</span> ({selectedDevices.length})
              </Button>
              <Button onClick={() => setShowBulkWiFiModal(true)} variant="outline" size="sm" className="border-blue-500 text-blue-600 text-xs h-8 dark:text-blue-400">
                <Wifi className="w-3 h-3 sm:mr-1" />
                <span className="hidden sm:inline">WiFi</span> ({selectedDevices.length})
              </Button>
            </>
          )}
          <Button onClick={handleSummonAll} variant="outline" size="sm" className="border-purple-500 text-purple-600 hover:bg-purple-50 text-xs h-8 dark:text-purple-400 dark:hover:bg-purple-900/30">
            <Zap className="w-3 h-3 sm:mr-1" />
            <span className="hidden sm:inline">Summon All</span> ({stats.online})
          </Button>
          <Button onClick={triggerManualSync} variant="outline" size="sm" disabled={syncing} className="text-xs h-8">
            <RefreshCw className={`w-3 h-3 sm:mr-1 ${syncing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{syncing ? 'Syncing...' : 'Sync'}</span>
          </Button>
          <Button onClick={fetchDevices} variant="outline" size="sm" className="text-xs h-8">
            <RefreshCw className="w-3 h-3" />
          </Button>
          <Button onClick={fetchACSDevices} size="sm" className="text-xs h-8">
            <Download className="w-3 h-3 sm:mr-1" />
            <span className="hidden sm:inline">Import</span>
          </Button>
          <Button onClick={() => setShowAddDeviceModal(true)} size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-xs h-8">
            <Plus className="w-3 h-3 sm:mr-1" />
            <span className="hidden sm:inline">Add</span>
          </Button>
        </div>
      </div>

      {/* Stats - Compact Design */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        <div className="bg-white p-2 sm:p-3 rounded-md border border-slate-200 hover:border-slate-300 transition-colors dark:bg-slate-800 dark:border-slate-700 dark:hover:border-slate-600">
          <p className="text-[10px] sm:text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5 dark:text-slate-400">Total</p>
          <p className="text-xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100">{stats.total_all}</p>
        </div>
        <div className="bg-white p-2 sm:p-3 rounded-md border border-slate-200 hover:border-green-300 transition-colors dark:bg-slate-800 dark:border-slate-700">
          <p className="text-[10px] sm:text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5 dark:text-slate-400">Online</p>
          <p className="text-xl sm:text-3xl font-bold text-green-600 dark:text-green-400">{stats.online}</p>
        </div>
        <div className="bg-white p-2 sm:p-3 rounded-md border border-slate-200 hover:border-red-300 transition-colors dark:bg-slate-800 dark:border-slate-700">
          <p className="text-[10px] sm:text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5 dark:text-slate-400">Offline</p>
          <p className="text-xl sm:text-3xl font-bold text-red-600 dark:text-red-400">{stats.offline}</p>
        </div>
        <div className="bg-white p-2 sm:p-3 rounded-md border border-slate-200 hover:border-slate-300 transition-colors dark:bg-slate-800 dark:border-slate-700 dark:hover:border-slate-600">
          <p className="text-[10px] sm:text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5 dark:text-slate-400">Customer</p>
          <p className="text-xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100">{stats.with_customer}</p>
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div className="bg-white rounded-md border border-slate-200 p-1 inline-flex gap-1 dark:bg-slate-800 dark:border-slate-700">
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium transition-all ${
            statusFilter === 'all'
              ? 'bg-indigo-600 text-white shadow-sm'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          All
          <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] bg-white/20">
            {stats.total_all}
          </span>
        </button>
        <button
          onClick={() => setStatusFilter('online')}
          className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium transition-all ${
            statusFilter === 'online'
              ? 'bg-green-600 text-white shadow-sm'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          🟢 <span className="hidden sm:inline">Online</span>
          <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] bg-white/20">
            {stats.online}
          </span>
        </button>
        <button
          onClick={() => setStatusFilter('offline')}
          className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium transition-all ${
            statusFilter === 'offline'
              ? 'bg-red-600 text-white shadow-sm'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          🔴 <span className="hidden sm:inline">Offline</span>
          <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] bg-white/20">
            {stats.offline}
          </span>
        </button>
      </div>

      {/* Search & Page Size - Compact */}
      <div className="bg-white p-2 sm:p-3 rounded-md border border-slate-200 dark:bg-slate-800 dark:border-slate-700">
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Search serial, device ID, IP..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 text-xs sm:text-sm border border-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:border-slate-700"
            />
          </div>
          <div className="flex items-center gap-2 whitespace-nowrap" data-testid="branch-filter-wrap">
            <span className="text-xs text-slate-500 dark:text-slate-400">Cabang:</span>
            <select
              value={selectedBranchId}
              onChange={(e) => { if (_isBranchScoped) return; setSelectedBranchId(e.target.value); setCurrentPage(1); }}
              disabled={_isBranchScoped}
              title={_isBranchScoped ? 'Anda hanya dapat melihat data cabang Anda sendiri' : undefined}
              className={"border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-[10rem] " + (_isBranchScoped ? "bg-slate-100 cursor-not-allowed" : "")}
              data-testid="branch-filter-select"
            >
              {!_isBranchScoped && <option value="">Semua Cabang</option>}
              {branches.filter(b => !_isBranchScoped || String(b.id) === _bsUserBranchId).map((b) => (
                <option key={b.id} value={String(b.id)}>{b.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span className="text-xs text-slate-500 dark:text-slate-400">Show:</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
            <span className="text-xs text-slate-500 dark:text-slate-400">per page</span>
          </div>
        </div>
      </div>

      {/* Selection Info Banner */}
      {selectedDevices.length > 0 && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4 dark:bg-indigo-900/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                {selectedDevices.length}
              </div>
              <div>
                <p className="text-sm font-semibold text-indigo-900">
                  {selectedDevices.length} device{selectedDevices.length > 1 ? 's' : ''} selected
                </p>
                <p className="text-xs text-indigo-700 dark:text-indigo-300">
                  Ready for bulk actions: Reboot, WiFi Config, or Assign Customer
                </p>
              </div>
            </div>
            <Button 
              onClick={() => setSelectedDevices([])} 
              variant="outline" 
              size="sm"
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-100 dark:text-indigo-300 dark:hover:bg-indigo-900/40"
            >
              Clear Selection
            </Button>
          </div>
        </div>
      )}

      {/* Device Table - Compact Design */}
      <div className="bg-white rounded-md border border-slate-200 overflow-hidden dark:bg-slate-800 dark:border-slate-700">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200 dark:bg-slate-900 dark:border-slate-700">
              <tr>
                <th className="px-2 py-2 text-left w-12">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedDevices.length === devices.length && devices.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-slate-400 text-indigo-600 focus:ring-indigo-500 focus:ring-2 w-5 h-5 cursor-pointer dark:text-indigo-400"
                      title="Select all devices"
                    />
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 transition-colors dark:text-slate-300 dark:hover:bg-slate-700"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center">
                    Status
                    <SortIcon field="status" />
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 transition-colors dark:text-slate-300 dark:hover:bg-slate-700"
                  onClick={() => handleSort('serial_number')}
                >
                  <div className="flex items-center">
                    Serial Number
                    <SortIcon field="serial_number" />
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 transition-colors dark:text-slate-300 dark:hover:bg-slate-700"
                  onClick={() => handleSort('model')}
                >
                  <div className="flex items-center">
                    Model
                    <SortIcon field="model" />
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 transition-colors dark:text-slate-300 dark:hover:bg-slate-700"
                  onClick={() => handleSort('external_ip')}
                >
                  <div className="flex items-center">
                    IP Address
                    <SortIcon field="external_ip" />
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 transition-colors dark:text-slate-300 dark:hover:bg-slate-700"
                  onClick={() => handleSort('tr069_ip')}
                >
                  <div className="flex items-center">
                    IP TR069
                    <SortIcon field="tr069_ip" />
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 transition-colors dark:text-slate-300 dark:hover:bg-slate-700"
                  onClick={() => handleSort('pppoe_username')}
                >
                  <div className="flex items-center">
                    PPPoE User
                    <SortIcon field="pppoe_username" />
                  </div>
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide dark:text-slate-300">Cabang</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide dark:text-slate-300">WiFi</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide dark:text-slate-300">Signal</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide dark:text-slate-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan="11" className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                    Loading devices...
                  </td>
                </tr>
              ) : devices.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                    {totalDevices === 0 
                      ? 'No devices found. Import from ACS to get started.'
                      : 'No devices match your search.'}
                  </td>
                </tr>
              ) : (
                getSortedDevices(
                  devices.filter(device => {
                    // Apply status filter
                    if (statusFilter === 'online') {
                      return device.status === 'online' || device.online === true;
                    }
                    if (statusFilter === 'offline') {
                      return device.status === 'offline' || device.online === false || !device.status;
                    }
                    return true; // 'all' shows everything
                  })
                ).map((device) => (
                  <tr key={device.device_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors dark:border-slate-700 dark:hover:bg-slate-700/50">
                    <td className="px-2 py-2">
                      <div className="flex items-center justify-center">
                        <input
                          type="checkbox"
                          checked={selectedDevices.includes(device.device_id)}
                          onChange={() => handleSelectDevice(device.device_id)}
                          className="rounded border-slate-400 text-indigo-600 focus:ring-indigo-500 focus:ring-2 w-5 h-5 cursor-pointer dark:text-indigo-400"
                          title={`Select ${device.serial_number}`}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        device.online 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {device.online ? 'Online' : 'Offline'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{device.serial_number}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">{device.mac_address}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-sm text-slate-700 dark:text-slate-200">{device.model || 'Unknown'}</div>
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-700 dark:text-slate-200">{device.external_ip || '-'}</td>
                    <td className="px-3 py-2">
                      <span className="text-sm text-slate-700 font-mono dark:text-slate-200">
                        {device.tr069_ip || device.management_ip || '-'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {device.pppoe_username || device.username ? (
                        <div>
                          <div className="text-sm font-medium text-slate-900 font-mono dark:text-slate-100">
                            {device.pppoe_username || device.username}
                          </div>
                          {device.customer_name && (
                            <div className="text-xs text-slate-500 dark:text-slate-400">{device.customer_name}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm dark:text-slate-500">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {device.branch_name ? (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-full border ${branchBadgeColor(device.branch_name)}`}
                          title={`Cabang: ${device.branch_name}`}
                          data-testid={`branch-badge-${device.serial_number || device.device_id}`}
                        >
                          {device.branch_name}
                        </span>
                      ) : (
                        <span className="text-slate-300 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {device.wifi_ssids && device.wifi_ssids.length > 0 ? (
                        <div className="text-sm">
                          <div className="font-medium">{device.wifi_ssids[0].ssid}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            {device.wifi_ssids.length} SSID(s)
                          </div>
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm dark:text-slate-500">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {device.rx_power ? (
                        <div className="text-sm">
                          <div>RX: {device.rx_power} dBm</div>
                          {device.tx_power && <div className="text-xs text-slate-500 dark:text-slate-400">TX: {device.tx_power} dBm</div>}
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm dark:text-slate-500">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedDevice(device);
                            setShowDetailModal(true);
                          }}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedDevice(device);
                            setShowWiFiModal(true);
                          }}
                        >
                          <Wifi className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleSummonDevice(device.device_id)}
                          title="Summon/Refresh device"
                        >
                          <Zap className="w-4 h-4 text-green-600 dark:text-green-400" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRemoteAccess(device)}
                          title="Remote Access (Web Management)"
                          disabled={!device.external_ip || !device.online}
                        >
                          <ExternalLink className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => rebootDevice(device.device_id)}
                        >
                          <Power className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Controls */}
        {totalDevices > 0 && (
          <div className="px-4 py-3 border-t flex items-center justify-between">
            <div className="text-sm text-slate-600 dark:text-slate-300">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalDevices)} of {totalDevices} devices
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                First
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              
              <div className="flex items-center gap-1">
                {/* Show page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <Button
                      key={pageNum}
                      size="sm"
                      variant={currentPage === pageNum ? "default" : "outline"}
                      onClick={() => handlePageChange(pageNum)}
                      className="w-8"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              
              <Button
                size="sm"
                variant="outline"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col dark:bg-slate-800">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Import Devices from ACS</h2>
              <p className="text-sm text-slate-600 mt-1 dark:text-slate-300">
                Select devices to import into database
              </p>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-2">
                {acsDevices.map((device) => (
                  <label
                    key={device.device_id}
                    className="flex items-center p-3 border rounded hover:bg-slate-50 cursor-pointer dark:hover:bg-slate-700/50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedForImport.includes(device.device_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedForImport([...selectedForImport, device.device_id]);
                        } else {
                          setSelectedForImport(selectedForImport.filter(id => id !== device.device_id));
                        }
                      }}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <div className="font-medium">{device.serial_number}</div>
                      <div className="text-sm text-slate-600 dark:text-slate-300">
                        {device.model} - {device.external_ip || 'No IP'}
                      </div>
                    </div>
                    <Badge variant={device.online ? "success" : "secondary"}>
                      {device.online ? 'Online' : 'Offline'}
                    </Badge>
                  </label>
                ))}
              </div>
            </div>

            <div className="p-6 border-t flex justify-between items-center">
              <div className="text-sm text-slate-600 dark:text-slate-300">
                {selectedForImport.length} device(s) selected
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowImportModal(false);
                    setSelectedForImport([]);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={importDevices}
                  disabled={importing || selectedForImport.length === 0}
                >
                  {importing ? 'Importing...' : `Import ${selectedForImport.length} Device(s)`}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* WiFi Config Modal */}
      {showWiFiModal && selectedDevice && (
        <WiFiConfigModal
          device={selectedDevice}
          onClose={() => setShowWiFiModal(false)}
          onSave={updateWiFi}
        />
      )}

      {/* Device Detail Modal */}
      {showDetailModal && selectedDevice && (
        <DeviceDetailModal
          device={selectedDevice}
          onClose={() => setShowDetailModal(false)}
          onRefresh={fetchDevices}
        />
      )}

      {/* Bulk WiFi Config Modal */}
      {showBulkWiFiModal && selectedDevices.length > 0 && (
        <BulkWiFiConfigModal
          deviceCount={selectedDevices.length}
          onClose={() => setShowBulkWiFiModal(false)}
          onSave={handleBulkWiFiConfig}
        />
      )}

      {/* Remote Access Modal */}
      {showRemoteModal && remoteDevice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 dark:bg-slate-800">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">Remote Access - {remoteDevice.serial_number}</h3>
              <button 
                onClick={() => {
                  setShowRemoteModal(false);
                  setRemoteDevice(null);
                  setRemoteUrl('');
                }}
                className="text-slate-500 hover:text-slate-700 dark:text-slate-400"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              {remoteLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400" />
                  <span className="ml-3 text-slate-600 dark:text-slate-300">Membuat port proxy...</span>
                </div>
              ) : remoteUrl ? (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 dark:bg-green-900/30">
                    <p className="text-green-800 font-medium">✅ Remote Access Ready!</p>
                    <p className="text-sm text-green-600 mt-1 dark:text-green-400">Device IP: {remoteDevice.external_ip}</p>
                  </div>
                  
                  <div className="bg-slate-50 rounded-lg p-4 dark:bg-slate-900">
                    <p className="text-sm text-slate-600 mb-2 dark:text-slate-300">Direct Access URL:</p>
                    <code className="text-sm bg-white p-3 rounded border block break-all font-mono text-blue-600 dark:bg-slate-800 dark:text-blue-400">
                      {remoteUrl}
                    </code>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button 
                      onClick={openRemoteInNewTab}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Buka di Tab Baru
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(remoteUrl);
                        toast.success('URL copied to clipboard!');
                      }}
                    >
                      Copy URL
                    </Button>
                  </div>
                  
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-4 dark:bg-green-900/30">
                    <p className="text-sm text-green-800">
                      <strong>✅ Port-based Proxy</strong> - Akses langsung seperti sistem billing lama. 
                      Tidak ada batasan waktu selama proxy aktif.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/30">
                  <p className="text-red-800 font-medium">❌ Device tidak dapat diakses</p>
                  <p className="text-sm text-red-600 mt-1 dark:text-red-400">
                    Pastikan L2TP tunnel aktif dan routing sudah dikonfigurasi dengan benar.
                  </p>
                </div>
              )}
            </div>
            <div className="p-4 border-t bg-slate-50 flex justify-end dark:bg-slate-900">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowRemoteModal(false);
                  setRemoteDevice(null);
                  setRemoteUrl('');
                }}
              >
                Tutup
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Device Modal */}
      {showAddDeviceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto dark:bg-slate-800">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-2xl font-bold">Tambah WAN Device Baru</h2>
              <p className="text-slate-600 mt-1 dark:text-slate-300">Manual register ONT/CPE device baru ke sistem</p>
            </div>

            <div className="p-6 space-y-6">
              {/* Device Info */}
              <div>
                <h3 className="font-semibold text-lg mb-4">Device Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">
                      Serial Number <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={newDevice.serial_number}
                      onChange={(e) => setNewDevice({...newDevice, serial_number: e.target.value})}
                      placeholder="000AC2-HG6145D2-XXXX"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">
                      Device ID <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={newDevice.device_id}
                      onChange={(e) => setNewDevice({...newDevice, device_id: e.target.value})}
                      placeholder="000AC2-HG6145D2-XXXX"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">
                      Manufacturer
                    </label>
                    <select
                      value={newDevice.manufacturer}
                      onChange={(e) => setNewDevice({...newDevice, manufacturer: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg dark:border-slate-600"
                    >
                      <option value="FiberHome">FiberHome</option>
                      <option value="Huawei">Huawei</option>
                      <option value="ZTE">ZTE</option>
                      <option value="ALU">ALU</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">
                      Model
                    </label>
                    <Input
                      value={newDevice.model}
                      onChange={(e) => setNewDevice({...newDevice, model: e.target.value})}
                      placeholder="HG6145D2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">
                      TR069 IP Address <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={newDevice.tr069_ip}
                      onChange={(e) => setNewDevice({...newDevice, tr069_ip: e.target.value})}
                      placeholder="10.3.96.1"
                    />
                    <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">IP untuk remote ONT</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">
                      MAC Address
                    </label>
                    <Input
                      value={newDevice.mac_address}
                      onChange={(e) => setNewDevice({...newDevice, mac_address: e.target.value})}
                      placeholder="00:0A:C2:XX:XX:XX"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">
                      Firmware Version
                    </label>
                    <Input
                      value={newDevice.firmware_version}
                      onChange={(e) => setNewDevice({...newDevice, firmware_version: e.target.value})}
                      placeholder="V1.0.0"
                    />
                  </div>
                </div>
              </div>

              {/* Customer Link (Optional) */}
              <div>
                <h3 className="font-semibold text-lg mb-4">Link ke Customer (Opsional)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">
                      Customer ID
                    </label>
                    <Input
                      value={newDevice.customer_id}
                      onChange={(e) => setNewDevice({...newDevice, customer_id: e.target.value})}
                      placeholder="customer-uuid"
                    />
                    <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Link ONT ke customer tertentu</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">
                      PPPoE Username
                    </label>
                    <Input
                      value={newDevice.pppoe_username}
                      onChange={(e) => setNewDevice({...newDevice, pppoe_username: e.target.value})}
                      placeholder="username@isp.com"
                    />
                    <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Untuk matching dengan customer</p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">
                  Notes / Catatan
                </label>
                <textarea
                  value={newDevice.notes}
                  onChange={(e) => setNewDevice({...newDevice, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg dark:border-slate-600"
                  rows="3"
                  placeholder="Catatan tambahan tentang device ini..."
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 dark:bg-blue-900/30">
                <h4 className="font-medium text-blue-900 mb-2">💡 Tips:</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Serial Number & Device ID biasanya sama untuk ONT FiberHome</li>
                  <li>• TR069 IP adalah IP lokal ONT untuk remote access</li>
                  <li>• Customer ID bisa diisi nanti via halaman WAN Devices</li>
                  <li>• Pastikan ONT sudah dikonfigurasi dengan ACS URL yang benar</li>
                </ul>
              </div>
            </div>

            <div className="p-6 bg-slate-50 border-t border-slate-200 flex justify-end gap-3 dark:bg-slate-900 dark:border-slate-700">
              <Button
                variant="outline"
                onClick={() => setShowAddDeviceModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddDevice}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Tambah Device
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        type={confirmDialog.type}
        action={confirmDialog.action}
        loading={confirmDialog.loading}
      />
    </div>
  );
};

// WiFi Config Modal Component
const WiFiConfigModal = ({ device, onClose, onSave }) => {
  const [ssidIndex, setSsidIndex] = useState(1);
  const [ssid, setSsid] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (device.wifi_ssids && device.wifi_ssids.length > 0) {
      const firstWifi = device.wifi_ssids[0];
      setSsidIndex(firstWifi.index);
      setSsid(firstWifi.ssid || '');
    }
  }, [device]);

  const handleSave = async () => {
    if (!ssid && !password) {
      toast.error('Please provide SSID or password');
      return;
    }

    if (password && password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setSaving(true);
    const data = { ssid_index: ssidIndex };
    if (ssid) data.ssid = ssid;
    if (password) data.password = password;

    await onSave(data);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full dark:bg-slate-800">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold">Change WiFi Settings</h2>
          <p className="text-sm text-slate-600 mt-1 dark:text-slate-300">{device.serial_number}</p>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">SSID Index</label>
            <select
              value={ssidIndex}
              onChange={(e) => setSsidIndex(parseInt(e.target.value))}
              className="w-full border rounded px-3 py-2"
            >
              {[1, 2, 3, 4].map(i => (
                <option key={i} value={i}>SSID {i}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">WiFi Name (SSID)</label>
            <Input
              value={ssid}
              onChange={(e) => setSsid(e.target.value)}
              placeholder="Enter new SSID (leave empty to keep current)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">WiFi Password</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password (min 8 characters)"
            />
            <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Leave empty to keep current password</p>
          </div>

          {device.wifi_ssids && device.wifi_ssids.length > 0 && (
            <div className="p-3 bg-slate-50 rounded text-sm dark:bg-slate-900">
              <p className="font-medium mb-1">Current WiFi Configs:</p>
              {device.wifi_ssids.map(wifi => (
                <div key={wifi.index} className="text-slate-600 dark:text-slate-300">
                  SSID {wifi.index}: {wifi.ssid}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Device Detail Modal Component  
const DeviceDetailModal = ({ device, onClose, onRefresh }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto dark:bg-slate-800">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold">Device Details</h2>
          <p className="text-sm text-slate-600 mt-1 dark:text-slate-300">{device.serial_number}</p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Device Info */}
          <div>
            <h3 className="font-semibold mb-3">Device Information</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-slate-600 dark:text-slate-300">Serial Number:</span>
                <div className="font-medium">{device.serial_number}</div>
              </div>
              <div>
                <span className="text-slate-600 dark:text-slate-300">Model:</span>
                <div className="font-medium">{device.model || 'Unknown'}</div>
              </div>
              <div>
                <span className="text-slate-600 dark:text-slate-300">MAC Address:</span>
                <div className="font-medium">{device.mac_address || '-'}</div>
              </div>
              <div>
                <span className="text-slate-600 dark:text-slate-300">Status:</span>
                <Badge variant={device.online ? "success" : "destructive"}>
                  {device.online ? 'Online' : 'Offline'}
                </Badge>
              </div>
            </div>
          </div>

          {/* Network Info */}
          <div>
            <h3 className="font-semibold mb-3">Network Information</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-slate-600 dark:text-slate-300">External IP:</span>
                <div className="font-medium">{device.external_ip || '-'}</div>
              </div>
              <div>
                <span className="text-slate-600 dark:text-slate-300">Connection:</span>
                <div className="font-medium">{device.connection_status || '-'}</div>
              </div>
              {device.pppoe_username && (
                <div>
                  <span className="text-slate-600 dark:text-slate-300">PPPoE Username:</span>
                  <div className="font-medium">{device.pppoe_username}</div>
                </div>
              )}
            </div>
          </div>

          {/* Signal Info */}
          {(device.rx_power || device.tx_power || device.temperature) && (
            <div>
              <h3 className="font-semibold mb-3">Signal Quality</h3>
              <div className="grid grid-cols-3 gap-3 text-sm">
                {device.rx_power && (
                  <div>
                    <span className="text-slate-600 dark:text-slate-300">RX Power:</span>
                    <div className="font-medium">{device.rx_power} dBm</div>
                  </div>
                )}
                {device.tx_power && (
                  <div>
                    <span className="text-slate-600 dark:text-slate-300">TX Power:</span>
                    <div className="font-medium">{device.tx_power} dBm</div>
                  </div>
                )}
                {device.temperature && (
                  <div>
                    <span className="text-slate-600 dark:text-slate-300">Temperature:</span>
                    <div className="font-medium">{device.temperature}°C</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* WiFi Info */}
          {device.wifi_ssids && device.wifi_ssids.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3">WiFi Configuration</h3>
              <div className="space-y-2">
                {device.wifi_ssids.map(wifi => (
                  <div key={wifi.index} className="p-3 border rounded">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-medium">{wifi.ssid}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">SSID {wifi.index}</div>
                      </div>
                      <Badge variant={wifi.enabled ? "success" : "secondary"}>
                        {wifi.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Customer Info */}
          {device.customer_name && (
            <div>
              <h3 className="font-semibold mb-3">Customer Assignment</h3>
              <div className="p-3 bg-slate-50 rounded dark:bg-slate-900">
                <div className="font-medium">{device.customer_name}</div>
                {device.customer_phone && (
                  <div className="text-sm text-slate-600 dark:text-slate-300">{device.customer_phone}</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t flex justify-end">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

// Bulk WiFi Config Modal Component
const BulkWiFiConfigModal = ({ deviceCount, onClose, onSave }) => {
  const [ssidIndex, setSsidIndex] = useState(1);
  const [ssid, setSsid] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!ssid && !password) {
      toast.error('Please provide SSID or password');
      return;
    }

    if (password && password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setSaving(true);
    const data = { ssid_index: ssidIndex };
    if (ssid) data.ssid = ssid;
    if (password) data.password = password;

    await onSave(data);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full dark:bg-slate-800">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold">Bulk WiFi Configuration</h2>
          <p className="text-sm text-slate-600 mt-1 dark:text-slate-300">Apply to {deviceCount} selected devices</p>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800 dark:bg-amber-900/30">
            ⚠️ This will change WiFi settings for all selected devices. This operation may take a few minutes.
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">SSID Index</label>
            <select
              value={ssidIndex}
              onChange={(e) => setSsidIndex(parseInt(e.target.value))}
              className="w-full border rounded px-3 py-2"
            >
              {[1, 2, 3, 4].map(i => (
                <option key={i} value={i}>SSID {i}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">New WiFi Name (SSID)</label>
            <Input
              value={ssid}
              onChange={(e) => setSsid(e.target.value)}
              placeholder="Enter new SSID (optional)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">New WiFi Password</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password (min 8 characters)"
            />
            <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Leave fields empty to keep current values</p>
          </div>
        </div>

        <div className="p-6 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Applying...' : `Apply to ${deviceCount} Devices`}
          </Button>
        </div>
      </div>

    </div>
  );
};

export default WANDevices;
